"use client";

import type { CSSProperties } from "react";

import { Clock, RefreshCw, Users, AlertCircle } from "lucide-react";

export default function WhyNotEnough() {
  return (
    <section className="py-24 bg-[rgb(var(--bg-base))] relative overflow-hidden" id="warum">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-[10%] w-[400px] h-[400px] rounded-full bg-[#3C2447]/8 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--text-primary))] mb-4">
              Warum einmal programmieren und ein bisschen Pflege nicht reicht
            </h2>
          </div>

          <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 rounded-2xl p-8 md:p-12 mb-12">
            <p className="text-lg text-[rgb(var(--text-muted-strong))] mb-6">
              Viele Angebote folgen diesem Ansatz:
            </p>
            <ul
              className="card-list mb-8 text-[rgb(var(--text-muted))]"
              style={{ "--bullet-color": "rgb(201, 179, 126)" } as CSSProperties}
            >
              <li>Website einmal sauber programmieren</li>
              <li>Gelegentliche Wartung oder Updates</li>
              <li>Hoffen, dass die Platzierung bei Google stabil bleibt</li>
            </ul>
            <div className="bg-gradient-to-r from-[#C9B37E]/10 to-[#3C2447]/10 border-l-4 border-[#C9B37E] p-4 rounded-r-lg">
              <p className="text-[rgb(var(--text-primary))] font-semibold">
                Für dauerhafte Sichtbarkeit bei Google reicht das nicht.
              </p>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))] mb-6 text-center">Der Grund ist einfach:</h3>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="group card-glow bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 hover:border-[#1B3A60]/40 rounded-xl p-6 transition-all" data-testid="card-reason-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B3A60]/30 to-[rgb(var(--bg-surface))] border border-[#1B3A60]/30 flex items-center justify-center mb-4">
                <RefreshCw className="w-5 h-5 text-[#4A7AB8]" />
              </div>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(74, 122, 184)" } as CSSProperties}
              >
                <li>Google bewertet Websites laufend neu.</li>
                <li>Ohne Monitoring schlägt jede Änderung negativ durch.</li>
              </ul>
            </div>
            <div className="group card-glow bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#3C2447]/20 hover:border-[#3C2447]/40 rounded-xl p-6 transition-all" data-testid="card-reason-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3C2447]/30 to-[rgb(var(--bg-surface))] border border-[#3C2447]/30 flex items-center justify-center mb-4">
                <Clock className="w-5 h-5 text-[#7A5A8A]" />
              </div>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(122, 90, 138)" } as CSSProperties}
              >
                <li>Jede Strukturänderung wirkt sofort auf Google.</li>
                <li>Nur ein stabiler Betrieb kann diese Reaktion abfedern.</li>
              </ul>
            </div>
            <div className="group card-glow bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#C9B37E]/15 hover:border-[#C9B37E]/30 rounded-xl p-6 transition-all" data-testid="card-reason-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9B37E]/20 to-[rgb(var(--bg-surface))] border border-[#C9B37E]/20 flex items-center justify-center mb-4">
                <AlertCircle className="w-5 h-5 text-[#C9B37E]" />
              </div>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(201, 179, 126)" } as CSSProperties}
              >
                <li>Kleine Fehler drücken die Platzierung.</li>
                <li>Tracking oder Links: Wenn sie fehlen, verliert man Leads.</li>
              </ul>
            </div>
            <div className="group card-glow bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 hover:border-[#1B3A60]/40 rounded-xl p-6 transition-all" data-testid="card-reason-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B3A60]/30 to-[rgb(var(--bg-surface))] border border-[#1B3A60]/30 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-[#4A7AB8]" />
              </div>
              <ul
                className="card-list text-sm text-[rgb(var(--text-muted-strong))]"
                style={{ "--bullet-color": "rgb(74, 122, 184)" } as CSSProperties}
              >
                <li>Andere verbessern ständig weiter.</li>
                <li>Ohne das Blickfeld auf Technik fehlt die Kontrolle.</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 text-[rgb(var(--text-primary))] rounded-2xl p-8 md:p-12 text-center">
            <p className="text-lg text-[rgb(var(--text-muted-strong))] mb-6">
              Einmal programmieren schafft nur eine Ausgangsbasis. Ohne laufende Überwachung und Pflege verliert diese Basis mit der Zeit an Wirkung.
            </p>
            <div className="inline-block bg-gradient-to-r from-[#1B3A60] via-[#2D5A8C] to-[#1B3A60] text-[rgb(var(--text-on-accent))] rounded-xl px-6 py-4 shadow-lg shadow-[#1B3A60]/30">
              <p className="font-bold text-xl">Sichtbarkeit bei Google ist kein Projekt mit Enddatum.</p>
              <p className="font-bold text-xl">Sie braucht dauerhaft saubere Technik.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
